import React, { useEffect, useState, useCallback } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import {
  listStockPendingActions,
  resolveStockPendingAction,
} from "../../services/invoicingServices";

// Bell global no Header — visível em todas as páginas. Faz polling
// a cada 60s para apanhar pending actions criadas em tempo-real
// pelo webhook Moloni (notas de crédito / facturas apagadas).
//
// O ícone só aparece se houver pending actions; caso contrário fica
// escondido para não poluir.
const POLL_INTERVAL_MS = 60 * 1000;

const StockAlertsBell = () => {
  const [actions, setActions] = useState([]);
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [partialQty, setPartialQty] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await listStockPendingActions("pending", {
        includeRecentAuto: true,
      });
      setActions(r?.data?.data?.rows || []);
    } catch (_) {
      // Silencioso: erros aqui são típicos quando há sessão expirada.
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  const resolve = async (action, resolution, qty = null) => {
    if (resolution === "partial" && (!qty || qty <= 0)) {
      toast.error("Indica uma quantidade válida");
      return;
    }
    const isBaixa = action.action_type === "invoice_external_baixa";
    if (resolution === "skip") {
      const ok = window.confirm(
        isBaixa
          ? "Ignorar esta factura externa? O stock TP NÃO será baixado e ficará dessincronizado com o que foi facturado no Moloni."
          : "Marcar como 'não repor stock'? Isto significa que as unidades vendidas continuam fora do inventário (ex: peças danificadas).",
      );
      if (!ok) return;
    }
    try {
      setResolving(action.id);
      const r = await resolveStockPendingAction(action.id, {
        resolution,
        qty,
      });
      const data = r?.data?.data;
      toast.success(
        resolution === "skip"
          ? "Marcado como ignorar"
          : isBaixa
          ? `${data?.restored || 0} unidade${data?.restored === 1 ? "" : "s"} - baixa stock`
          : `${data?.restored || 0} unidade${data?.restored === 1 ? "" : "s"} repostas`,
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setResolving(null);
    }
  };

  // Apenas pending count para o badge — auto-resolvidas são informativas
  // e ficam só dentro do modal (não disparam alerta).
  const pendingActions = actions.filter((a) => a.status === "pending");
  const autoActions = actions.filter((a) => a.status !== "pending");
  const count = pendingActions.length;
  // Esconde completamente o botão se não há nada a mostrar
  // (sem pendentes E sem histórico relevante).
  if (count === 0 && autoActions.length === 0) return null;
  const hasPending = count > 0;
  const accentColor = hasPending ? "#dc3545" : "#6c757d";
  const accentBg = hasPending ? "#fff5f6" : "#f8f9fa";
  const accentBorder = hasPending ? "#f5c2c7" : "#dee2e6";

  return (
    <li
      className="nav-item"
      style={{ marginRight: "8px", display: "flex", alignItems: "center" }}
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={
          hasPending
            ? `${count} alerta${count === 1 ? "" : "s"} de stock por resolver`
            : "Histórico de alertas de stock"
        }
        style={{
          background: accentBg,
          border: `1px solid ${accentBorder}`,
          color: accentColor,
          borderRadius: 6,
          padding: "4px 10px",
          position: "relative",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
        }}
      >
        <i className="bi bi-bell-fill" style={{ color: accentColor }} />
        <span style={{ fontWeight: 500 }}>Stock</span>
        {hasPending && (
          <span
            style={{
              backgroundColor: accentColor,
              color: "#fff",
              borderRadius: 10,
              padding: "0 6px",
              fontSize: 11,
              minWidth: 18,
              textAlign: "center",
            }}
          >
            {count}
          </span>
        )}
      </button>

      <Modal isOpen={open} toggle={() => setOpen(false)} size="lg">
        <ModalHeader toggle={() => setOpen(false)}>
          Alertas de stock
        </ModalHeader>
        <ModalBody>
          {pendingActions.length > 0 && (
            <>
              <h6 style={{ marginBottom: 8 }}>
                <i
                  className="bi bi-exclamation-triangle-fill"
                  style={{ color: "#c9302c", marginRight: 6 }}
                />
                Pendentes de decisão ({pendingActions.length})
              </h6>
              <p style={{ fontSize: 13, color: "#6c757d" }}>
                Quando uma factura final é apagada ou recebe nota de crédito
                (parcial ou total), o stock TP <strong>não</strong> é reposto
                automaticamente. Decide aqui: <strong>Repor</strong> (peça
                volta ao inventário), <strong>Repor parcial</strong> (só N
                unidades), ou <strong>Não repor</strong> (peça
                danificada/perdida).
              </p>
            </>
          )}
          {pendingActions.map((a) => {
            const isResolving = resolving === a.id;
            const isBaixa = a.action_type === "invoice_external_baixa";
            return (
              <div
                key={a.id}
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 10,
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 13,
                  }}
                >
                  <div>
                    <strong>
                      {a.document_label || `Factura #${a.invoice_number || a.document_id}`}
                    </strong>
                    {a.entity_name && <> — {a.entity_name}</>}
                    {a.entity_vat && (
                      <small className="text-muted">
                        {" "}
                        · NIF {a.entity_vat}
                      </small>
                    )}
                  </div>
                  <span
                    className="badge bg-secondary"
                    style={{ fontSize: 10, alignSelf: "center" }}
                  >
                    {a.action_type === "credit_note_emitted"
                      ? "Nota de crédito"
                      : a.action_type === "credit_note_partial"
                      ? "Nota de crédito parcial"
                      : a.action_type === "invoice_deleted"
                      ? "Documento apagado"
                      : a.action_type === "invoice_canceled"
                      ? "Documento cancelado"
                      : a.action_type === "invoice_external_baixa"
                      ? "Factura externa (baixa pendente)"
                      : a.action_type === "invoice_finalized_externally"
                      ? "Rascunho finalizado no Moloni"
                      : a.action_type}
                  </span>
                </div>
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  Peça:{" "}
                  <strong>
                    {a.parts_internal_id ? `#${a.parts_internal_id}` : ""}{" "}
                    {a.part_name || `parts_id=${a.parts_id}`}
                  </strong>
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: "#6c757d" }}>
                  {isBaixa ? (
                    <>
                      Facturado: <strong>{a.requested_qty}</strong> · Disponível
                      para baixa: <strong>{a.available_qty}</strong> unidades
                    </>
                  ) : (
                    <>
                      Pedido: <strong>{a.requested_qty}</strong> · Disponível
                      para reposição: <strong>{a.available_qty}</strong>{" "}
                      unidades
                    </>
                  )}
                </div>
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      isBaixa ? "btn-primary" : "btn-success"
                    }`}
                    onClick={() => resolve(a, isBaixa ? "baixa" : "restore")}
                    disabled={isResolving || a.available_qty === 0}
                  >
                    {isBaixa ? (
                      <>
                        <i className="bi bi-box-arrow-down"></i> Baixar todas (
                        {a.available_qty})
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-counterclockwise"></i> Repor
                        todas ({a.available_qty})
                      </>
                    )}
                  </button>
                  <div style={{ display: "flex", gap: 4 }}>
                    <input
                      type="number"
                      min={1}
                      max={a.available_qty}
                      placeholder="N"
                      style={{ width: 70 }}
                      className="form-control form-control-sm"
                      value={partialQty[a.id] ?? ""}
                      onChange={(e) =>
                        setPartialQty((p) => ({
                          ...p,
                          [a.id]: e.target.value,
                        }))
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-warning"
                      onClick={() =>
                        resolve(a, "partial", Number(partialQty[a.id]))
                      }
                      disabled={isResolving}
                    >
                      {isBaixa ? "Baixar parcial" : "Repor parcial"}
                    </button>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => resolve(a, "skip")}
                    disabled={isResolving}
                  >
                    {isBaixa ? "Ignorar" : "Não repor"}
                  </button>
                </div>
              </div>
            );
          })}

          {autoActions.length > 0 && (
            <>
              <h6 style={{ marginTop: 16, marginBottom: 8 }}>
                <i
                  className="bi bi-check-circle-fill"
                  style={{ color: "#198754", marginRight: 6 }}
                />
                Baixas de stock recentes ({autoActions.length})
              </h6>
              <p style={{ fontSize: 12, color: "#6c757d" }}>
                Histórico (últimos 7 dias) de baixas de stock TP por
                facturas: <span style={{ color: "#198754" }}>verde</span> =
                emitida via TP,{" "}
                <span style={{ color: "#0d6efd" }}>azul</span> = factura
                externa (Moloni portal). Lista informativa.
              </p>
              {autoActions.map((a) => {
                const isInternal = a.action_type === "invoice_internal_baixa";
                const isExternalBaixa =
                  a.action_type === "invoice_external_baixa";
                const isRestore =
                  !isInternal &&
                  !isExternalBaixa &&
                  (a.status === "resolved_restored" ||
                    a.status === "resolved_partial");
                const accentColor = isInternal
                  ? "#198754"
                  : isRestore
                  ? "#0f766e"
                  : "#0d6efd";
                const bgColor = isInternal
                  ? "#f6fbf7"
                  : isRestore
                  ? "#f0fdfa"
                  : "#f8fbff";
                const borderColor = isInternal
                  ? "#d1e7dd"
                  : isRestore
                  ? "#99f6e4"
                  : "#cfe2ff";
                const badgeText = isInternal
                  ? "Baixa via TP"
                  : isExternalBaixa
                  ? "Baixa automática"
                  : isRestore
                  ? "Reposição stock"
                  : a.status === "resolved_skipped"
                  ? "Ignorada"
                  : "Resolvida";
                return (
                  <div
                    key={a.id}
                    style={{
                      border: `1px solid ${borderColor}`,
                      borderLeft: `3px solid ${accentColor}`,
                      borderRadius: 6,
                      padding: 10,
                      marginBottom: 8,
                      background: bgColor,
                      fontSize: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <div>
                        <strong>
                          {a.document_label ||
                            `${isInternal ? "Factura TP" : "Factura externa"} #${a.invoice_number || a.document_id}`}
                        </strong>
                        {a.entity_name && <> — {a.entity_name}</>}
                      </div>
                      <span
                        className="badge"
                        style={{
                          fontSize: 10,
                          alignSelf: "center",
                          backgroundColor: accentColor,
                          color: "#fff",
                        }}
                      >
                        {badgeText}
                      </span>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      Peça:{" "}
                      <strong>
                        {a.parts_internal_id ? `#${a.parts_internal_id}` : ""}{" "}
                        {a.part_name || `parts_id=${a.parts_id}`}
                      </strong>{" "}
                      · <strong>{a.resolved_qty || 0}</strong> unidade
                      {(a.resolved_qty || 0) === 1 ? "" : "s"} baixada
                      {(a.resolved_qty || 0) === 1 ? "" : "s"}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {pendingActions.length === 0 && autoActions.length === 0 && (
            <p style={{ color: "#6c757d", fontSize: 13 }}>Nada por aqui.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setOpen(false)}
          >
            Fechar
          </button>
        </ModalFooter>
      </Modal>
    </li>
  );
};

export default StockAlertsBell;
